import { http } from './request'
import type { ApiResponse } from '@/types'
import {
  buildApiUrl,
  buildAuthHeaders,
  HttpError,
  runUnauthorizedHandler
} from '@/api/request'

export interface AiToolCallView {
  callId: string
  toolName: string
  /** 后端工具注册表提供的短中文动作标签。 */
  displayLabel?: string | null
  argumentsJson: string
  success: boolean
  resultJson: string | null
  errorMessage: string | null
  durationMs: number
  /** Gateway-only repair attempts are retained in audit logs but not shown as user-facing failures. */
  displayDisposition?: 'VISIBLE' | 'INTERNAL_REPAIR'
  status?: string
  /** 与后端 {@code AiCapabilityKind} 一致；仅 {@code EXECUTION} 表示可能产生写库/删改等副作用 */
  capabilityKind?: string | null
  /** 工具阶段墙钟超过阈值时 true，前端可显示「正在规划下一步」直到下一段模型输出 */
  postToolPlanningHint?: boolean
  argumentViewJson?: string | null
  resultViewJson?: string | null
}

/** 流式对话中：工具已下发、结果未返回时为 true */
export type AiToolCallUi = AiToolCallView & {
  pending?: boolean
  /** 收到 tool_start 时由前端记录，用于「已等待 N 秒」 */
  startedAt?: number
  /** 前端时间线合并计数：连续同名工具调用被折叠后的次数（展示用，非后端字段） */
  repeatCount?: number
  /** 材料探索 SSE 进度文案（默认不带 Python） */
  progressHint?: string | null
}

export interface AiChatResponse {
  sessionId: string
  conversationId: number
  assistantMessage: string
  reasoningContent: string | null
  toolCalls: AiToolCallView[]
  finishReason: string
  promptTokens: number
  completionTokens: number
  totalTokens: number
  /** 本回合写入后的会话标题；首条消息时由服务端生成 */
  conversationTitle?: string | null
  /** true 表示标题仍在异步生成中，前端应保持 titlePending 状态 */
  titlePending?: boolean
  /** 任务耗时（毫秒）：run_done COMPLETED 时由服务端下发（与终行 taskDurationMs 同源），驱动实时折叠条 */
  durationMs?: number | null
}

/** 模型来源：OFFICIAL 官方内置 / TENANT 学校共享 / USER 个人自建 */
export type AiModelSource = 'OFFICIAL' | 'TENANT' | 'USER'

/** A model that the active server-side provider can invoke for the current user. */
export interface AiAvailableModel {
  apiId: string
  label: string
  /** The model is constrained to read-only tool access for this user/provider. */
  limited: boolean
  defaultModel: boolean
  /** 模型来源；后端合并官方与自定义模型后下发，前端据此渲染徽标。 */
  source?: AiModelSource
}

/**
 * 自定义模型视图（不含 apiKey）。对应后端 CustomModelService.CustomModelView。
 * 由 GET /api/ai/chat/models/custom 返回；GET /api/ai/chat/models 也会把自定义模型
 * 合并进 AiAvailableModel 列表（带 source=TENANT/USER）。
 */
export interface AiCustomModelView {
  id: number
  apiId: string
  label: string
  baseUrl: string
  model: string
  /** TENANT=学校共享（管理员创建） / USER=个人自建（任意用户可建） */
  scope: 'TENANT' | 'USER'
  /** 当前用户是否有权删除该模型。 */
  deletable: boolean
  /** 最大上下文窗口（token）；空则运行时查目录再走全局默认 */
  contextWindowTokens?: number | null
}

export interface CreateCustomModelPayload {
  label: string
  baseUrl: string
  apiKey: string
  model: string
  scope: 'TENANT' | 'USER'
  contextWindowTokens?: number | null
}

/** 更新学校共享模型（仅 TENANT scope、仅管理员）。apiKey 留空表示不更换密钥。 */
export interface UpdateCustomModelPayload {
  label: string
  baseUrl: string
  /** 留空表示不更换密钥 */
  apiKey?: string
  model: string
  contextWindowTokens?: number | null
}

export interface TestCustomModelPayload {
  baseUrl: string
  apiKey: string
  model: string
}

export interface TestCustomModelResult {
  success: boolean
  message: string
}

export interface AiUserQuestionOptionView {
  id: string
  label: string
}

export interface AiMessageMaterialAttachment {
  materialId: string
  fileName: string
}

export interface AiRecallUserMessageResponse {
  message: string
  materialIds: string[]
  materialAttachments: AiMessageMaterialAttachment[]
}

/**
 * 会话产物（沙盒生成的文件）分页预览响应。
 *
 * <p>后端 {@code /ai/chat/sessions/{sessionId}/artifacts/{artifactId}/preview} 端点：
 * <ul>
 *   <li>{@code previewType}="table"：headers + rows（cells 为列名→文本的键值对）</li>
 *   <li>{@code previewType}="text"：rows（text 为单行文本）</li>
 * </ul>
 * </p>
 */
export interface ArtifactPreviewRow {
  /** 行号（1 起） */
  lineNo: number
  /** table 预览：列名 → 单元格文本 */
  cells?: Record<string, string>
  /** text 预览：整行文本 */
  text?: string
}

export interface ArtifactPreviewResponse {
  artifactId: string
  filename: string
  fileType: string
  previewType: 'table' | 'text'
  headers: string[]
  rows: ArtifactPreviewRow[]
  total: number
  offset: number
  returned: number
}

/** 会话产物列表项：替代旧草稿工作台的列表视图。 */
export interface ArtifactSummary {
  artifactId: string
  filename: string
  fileType: string
  bytes: number
  createdAt: string
}

export interface AiMessageView {
  id: number
  role: 'user' | 'assistant' | 'tool' | 'system'
  content: string | null
  reasoningContent: string | null
  toolName: string | null
  toolDisplayLabel?: string | null
  toolCallId: string | null
  toolArgumentsJson: string | null
  toolResultJson: string | null
  toolError: string | null
  createdAt: string
  /** 工具行：注册表中的能力平面（KNOWLEDGE / PREFLIGHT / EXECUTION） */
  toolCapabilityKind?: string | null
  materialAttachments?: AiMessageMaterialAttachment[] | null
  toolArgumentsViewJson?: string | null
  toolResultViewJson?: string | null
  routeType?: string | null
  /** v1.1.44+ 折叠态支持：消息行状态
   * - 'INTERIM' 旧版过程消息（已不再写入）
   * - 'ROUND_REPLY' V2 主循环中间轮快照
   * - 'ACTIVE' / null 最终回合一行的默认状态
   */
  status?: string | null
  /** v1.1.44+ 折叠态支持：仅最终 ACTIVE 消息且正常结束时写入；
   *  表示『用户发消息 → 最终回复完成』的 wall clock 毫秒数 */
  taskDurationMs?: number | null
  /** role=user 时该标记为 true 表示这是主代理内部桥接文本（非真实用户输入），
   *  前端应隐藏该消息，不作为独立气泡显示，也不参与回合边界计算。
   *  与后端 AiMessageEntity.isSystemBridge 对应。 */
  isSystemBridge?: boolean | null
}

export interface AiStreamStateView {
  streamRunId: string
  sessionId: string
  conversationId: number | null
  streamKind: string | null
  phase: string | null
  status: string | null
  recoverable: boolean
  recoveryAction: string | null
  errorMessage: string | null
  startedAt: string | null
  updatedAt: string | null
}

export interface AiV2PendingActionView {
  pendingId: string
  toolCallId?: string
  kind: 'QUESTION' | 'CONFIRMATION'
  status: string
  createdAt?: string
  expiresAt?: string
  prompt?: string
  options?: AiUserQuestionOptionView[]
  customAllowed?: boolean
  questionGroupId?: string | null
  toolName?: string
  displayLabel?: string
  riskLevel?: string
  effectiveRisk?: string
  requiresRiskAcknowledgement?: boolean
  argumentsJson?: string
  preflightReport?: Record<string, unknown>
}

export interface AiChatRequest {
  sessionId?: string | null
  message: string
  /** 传给后端的 LLM model 参数；缺省时由服务端使用默认模型 */
  model?: string | null
  materialIds?: string[]
}

/** 侧边栏会话摘要（服务端表 ai_conversations + 内存中 AiSessionRunRegistry 的实时状态）。 */
export interface AiSessionSummary {
  sessionId: string
  title: string
  updatedAt: string
  titlePending?: boolean
  /**
   * 该会话的 AI 任务是否仍在后端跑（与前端订阅无关）。
   * 业务上用来在侧边栏把"删除按钮"替换成"转动圆圈"。
   */
  running?: boolean
  /**
   * 该会话等待用户处理的待办卡数量（提问卡 + 确认卡，status=PENDING）。
   * 刷新后据此在侧边栏点亮"需要操作"铃铛，不依赖前端内存队列。
   */
  pendingActionCount?: number
}

const STREAM_ACCEPT = 'text/event-stream'

export type AiChatStreamHandlers = {
  onEvent: (eventName: string, data: unknown) => void
  signal?: AbortSignal
}

/** 与后端 SseEmitter 30 分钟超时对齐；心跳事件会重置空闲计时。 */
const SSE_IDLE_TIMEOUT_MS = 30 * 60 * 1000
const SSE_FIRST_EVENT_TIMEOUT_MS = 60_000
/** fetch 建立连接/收到响应头的上限；与 Vite proxy timeout:0 配合，避免无限挂起 */
const SSE_FETCH_CONNECT_TIMEOUT_MS = 30_000

const NETWORK_ERROR_PATTERNS = [
  'network error',
  'failed to fetch',
  'networkerror',
  'load failed',
  'net::err_',
  'econnrefused',
  'socket hang up',
  'getsockopt',
  'permission denied',
  'connection reset',
  'connection refused',
  'broken pipe',
  'stream failed',
  'llm_stream_error',
  'deepseek stream'
]

const STREAM_INTERRUPT_USER_MESSAGE =
  '与 AI 服务的连接中断了。请检查网络后发送「继续」以恢复任务。'

/** Maps provider/transport technical failures to a short user-facing message. */
export function formatSseStreamError(err: unknown): string {
  const raw =
    err instanceof HttpError || err instanceof Error
      ? err.message
      : typeof err === 'string'
        ? err
        : ''
  const normalized = raw.trim().toLowerCase()
  if (!normalized) {
    return 'AI 调用失败'
  }
  if (
    normalized.includes('ai 响应超时')
    || normalized.includes('首包超时')
    || normalized.includes('连接 ai 服务超时')
  ) {
    return raw
  }
  if (NETWORK_ERROR_PATTERNS.some((token) => normalized.includes(token))) {
    return STREAM_INTERRUPT_USER_MESSAGE
  }
  // Already localized product copy from the backend — keep as-is.
  if (/[\u4e00-\u9fff]/.test(raw) && !normalized.includes('deepseek') && !normalized.includes('getsockopt')) {
    return raw
  }
  return 'AI 服务暂时无法完成回复，请稍后重试或发送「继续」。'
}

function linkedAbortSignal(userSignal?: AbortSignal): { signal: AbortSignal; dispose: () => void } {
  const ctrl = new AbortController()
  const abortWith = (reason?: unknown): void => {
    if (!ctrl.signal.aborted) {
      ctrl.abort(reason)
    }
  }
  userSignal?.addEventListener('abort', () => abortWith(userSignal.reason), { once: true })
  if (userSignal?.aborted) {
    abortWith(userSignal.reason)
  }
  const timer = setTimeout(
    () => abortWith(new Error('连接 AI 服务超时，请确认后端已启动（18080）并重试')),
    SSE_FETCH_CONNECT_TIMEOUT_MS
  )
  return {
    signal: ctrl.signal,
    dispose: () => clearTimeout(timer)
  }
}

async function postSseStream(url: string, init: RequestInit, handlers: AiChatStreamHandlers): Promise<void> {
  const linked = linkedAbortSignal(handlers.signal)
  try {
    const response = await fetch(url, {
      ...init,
      cache: 'no-store',
      credentials: 'include',
      signal: linked.signal
    })
    linked.dispose()

    if (response.status === 401) {
      const error = new HttpError({
        message: '登录状态已失效，请重新登录',
        status: 401,
        code: '401'
      })
      await runUnauthorizedHandler(error)
      throw error
    }

    if (!response.ok) {
      let detail = '请求失败，请稍后重试'
      try {
        const errBody = (await response.json()) as { message?: string }
        if (errBody?.message) detail = errBody.message
      } catch {
        // ignore
      }
      throw new HttpError({ message: detail, status: response.status, code: String(response.status) })
    }

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes(STREAM_ACCEPT) && !contentType.includes('event-stream')) {
      throw new Error('对话连接异常，请稍后重试')
    }

    await readSseResponse(response, handlers.onEvent)
  } finally {
    linked.dispose()
  }
}

/** POST SSE：首包 {@code session}（持久化后的真实 sessionId），随后增量推理/正文、工具起止、最终 {@code done}（载荷同 {@link AiChatResponse}）。 */
export async function postAiChatStream(
  payload: AiChatRequest,
  handlers: AiChatStreamHandlers
): Promise<void> {
  await postSseStream(
    buildApiUrl('/ai/chat/messages/stream'),
    {
      method: 'POST',
      headers: buildAuthHeaders({
        Accept: STREAM_ACCEPT,
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        sessionId: payload.sessionId ?? null,
        message: payload.message,
        model: payload.model ?? null,
        materialIds: payload.materialIds ?? []
      })
    },
    handlers
  )
}

/** 用户确认工具执行后：流式生成助手说明（与主对话 SSE 事件名一致）。 */
async function readSseResponse(
  response: Response,
  onEvent: (eventName: string, data: unknown) => void
): Promise<void> {
  let firstEventReceived = false
  let firstEventTimer: ReturnType<typeof setTimeout> | null = null

  const onEventTracked = async (eventName: string, data: unknown): Promise<void> => {
    if (!firstEventReceived) {
      firstEventReceived = true
      if (firstEventTimer != null) {
        clearTimeout(firstEventTimer)
        firstEventTimer = null
      }
    }
    await onEvent(eventName, data)
  }

  const firstEventPromise = new Promise<never>((_, reject) => {
    firstEventTimer = setTimeout(() => {
      reject(new Error('等待 AI 首包超时，请确认后端已启动且网络可达'))
    }, SSE_FIRST_EVENT_TIMEOUT_MS)
  })

  try {
    await Promise.race([
      readSseResponseBody(response, onEventTracked),
      firstEventPromise
    ])
  } finally {
    if (firstEventTimer != null) {
      clearTimeout(firstEventTimer)
    }
  }
}

async function readSseResponseBody(
  response: Response,
  onEvent: (eventName: string, data: unknown) => void
): Promise<void> {
  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Chat stream is not readable')
  }

  const decoder = new TextDecoder()
  let buffer = ''
  let lastActivity = Date.now()
  let idleTimer: ReturnType<typeof setTimeout> | null = null

  const scheduleIdleTimeout = (): void => {
    if (idleTimer != null) {
      clearTimeout(idleTimer)
    }
    const elapsed = Date.now() - lastActivity
    const remaining = Math.max(0, SSE_IDLE_TIMEOUT_MS - elapsed)
    idleTimer = setTimeout(() => {
      void reader.cancel(new Error('AI 响应超时，请重试'))
    }, remaining)
  }

  scheduleIdleTimeout()

  try {
    while (true) {
      const result = await reader.read()
      if (result.done) {
        buffer += decoder.decode()
        await flushSseBuffer(buffer, onEvent)
        return
      }

      lastActivity = Date.now()
      scheduleIdleTimeout()
      buffer += decoder.decode(result.value, { stream: true }).replace(/\r\n/g, '\n').replace(/\r/g, '\n')

      let sep = buffer.indexOf('\n\n')
      while (sep !== -1) {
        const chunk = buffer.slice(0, sep)
        buffer = buffer.slice(sep + 2)
        await dispatchSseChunk(chunk, onEvent)
        sep = buffer.indexOf('\n\n')
      }
    }
  } finally {
    if (idleTimer != null) {
      clearTimeout(idleTimer)
    }
  }
}

async function flushSseBuffer(buffer: string, onEvent: (eventName: string, data: unknown) => void): Promise<void> {
  const trimmed = buffer.trim()
  if (trimmed) {
    await dispatchSseChunk(trimmed, onEvent)
  }
}

async function dispatchSseChunk(
  chunk: string,
  onEvent: (eventName: string, data: unknown) => void
): Promise<void> {
  const parsed = parseSseBlock(chunk)
  if (!parsed) {
    return
  }
  let data: unknown = parsed.dataRaw
  try {
    data = JSON.parse(parsed.dataRaw)
  } catch {
    // keep raw string
  }
  await onEvent(parsed.eventName, data)
}

function parseSseBlock(chunk: string): { eventName: string; dataRaw: string } | null {
  const lines = chunk.split('\n')
  let eventName = 'message'
  const dataLines: string[] = []

  for (const rawLine of lines) {
    const line = rawLine.trimEnd()
    if (!line || line.startsWith(':')) {
      continue
    }
    const colon = line.indexOf(':')
    const field = colon >= 0 ? line.slice(0, colon) : line
    let value = colon >= 0 ? line.slice(colon + 1) : ''
    if (value.startsWith(' ')) {
      value = value.slice(1)
    }

    if (field === 'event') {
      eventName = value
      continue
    }
    if (field === 'data') {
      dataLines.push(value)
    }
  }

  if (dataLines.length === 0) {
    return null
  }

  return { eventName, dataRaw: dataLines.join('\n') }
}

export async function postAiRegenerateStream(
  sessionId: string,
  userMessageId: number,
  handlers: AiChatStreamHandlers & { model?: string | null }
): Promise<void> {
  await postSseStream(
    buildApiUrl(
      `/ai/chat/sessions/${encodeURIComponent(sessionId)}/messages/user/${userMessageId}/regenerate/stream`
    ),
    {
      method: 'POST',
      headers: buildAuthHeaders({
        Accept: STREAM_ACCEPT,
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({ model: handlers.model ?? null })
    },
    handlers
  )
}

/** v2 pending actions resume from their persisted AgentLoop checkpoint on a journaled SSE run. */
export async function postAiV2ResumeStream(
  payload: {
    pendingId: string
    actionType: 'QUESTION' | 'CONFIRMATION'
    userChoice: string
    extraPayload?: Record<string, unknown>
  },
  handlers: AiChatStreamHandlers
): Promise<void> {
  await postSseStream(
    buildApiUrl('/ai/v2/chat/resume/stream'),
    {
      method: 'POST',
      headers: buildAuthHeaders({
        Accept: STREAM_ACCEPT,
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(payload)
    },
    handlers
  )
}

/**
 * 继续回合：上一回合异常停止（步数上限/预算熔断/引擎错误）后，用户点击"继续"触发。
 * 不携带用户消息——后端从历史重建上下文，模型从停点继续；事件名与主对话 SSE 完全一致。
 */
export async function postAiContinueRunStream(
  sessionId: string,
  handlers: AiChatStreamHandlers
): Promise<void> {
  await postSseStream(
    buildApiUrl('/ai/chat/sessions/continue/stream'),
    {
      method: 'POST',
      headers: buildAuthHeaders({
        Accept: STREAM_ACCEPT,
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({ sessionId })
    },
    handlers
  )
}

export const aiChatApi = {
  listAvailableModels(): Promise<ApiResponse<AiAvailableModel[]>> {
    return http.get<AiAvailableModel[]>('/ai/chat/models')
  },
  /** 列出当前用户可见的自定义模型（学校共享 + 本人个人）。 */
  listCustomModels(): Promise<ApiResponse<AiCustomModelView[]>> {
    return http.get<AiCustomModelView[]>('/ai/chat/models/custom')
  },
  /** 创建自定义模型。TENANT scope 需学校管理员；USER scope 任意用户可建。 */
  createCustomModel(payload: CreateCustomModelPayload): Promise<ApiResponse<AiCustomModelView>> {
    return http.post<AiCustomModelView>('/ai/chat/models/custom', payload)
  },

  updateCustomModel(
    id: number,
    payload: UpdateCustomModelPayload
  ): Promise<ApiResponse<AiCustomModelView>> {
    return http.patch<AiCustomModelView>(`/ai/chat/models/custom/${id}`, payload)
  },
  /** 测试连接：用提供的地址/密钥/模型名发一条 ping，不落库。 */
  testCustomModel(payload: TestCustomModelPayload): Promise<ApiResponse<TestCustomModelResult>> {
    return http.post<TestCustomModelResult>('/ai/chat/models/custom/test', payload)
  },
  /** 删除自定义模型。USER scope 仅本人；TENANT scope 仅管理员。 */
  deleteCustomModel(id: number): Promise<ApiResponse<unknown>> {
    return http.delete(`/ai/chat/models/custom/${id}`)
  },
  listSessions(): Promise<ApiResponse<AiSessionSummary[]>> {
    return http.get<AiSessionSummary[]>('/ai/chat/sessions')
  },
  deleteSession(sessionId: string): Promise<ApiResponse<unknown>> {
    return http.delete(`/ai/chat/sessions/${encodeURIComponent(sessionId)}`)
  },
  listMessages(sessionId: string): Promise<ApiResponse<AiMessageView[]>> {
    return http.get<AiMessageView[]>(`/ai/chat/sessions/${encodeURIComponent(sessionId)}/messages`)
  },
  getStreamState(sessionId: string): Promise<ApiResponse<AiStreamStateView | null>> {
    return http.get<AiStreamStateView | null>(
      `/ai/chat/sessions/${encodeURIComponent(sessionId)}/stream-state`
    )
  },
  getV2PendingActions(sessionId: string): Promise<ApiResponse<{
    sessionId: string
    count: number
    pendingActions: AiV2PendingActionView[]
  }>> {
    return http.get(`/ai/v2/conversations/${encodeURIComponent(sessionId)}/pending`)
  },
  recallUserMessage(
    sessionId: string,
    userMessageId: number
  ): Promise<ApiResponse<AiRecallUserMessageResponse>> {
    return http.post<AiRecallUserMessageResponse>(
      `/ai/chat/sessions/${encodeURIComponent(sessionId)}/messages/user/${userMessageId}/recall`,
      {}
    )
  },
  listSessionArtifacts(sessionId: string): Promise<ApiResponse<ArtifactSummary[]>> {
    return http.get<ArtifactSummary[]>(
      `/ai/chat/sessions/${encodeURIComponent(sessionId)}/artifacts`
    )
  },
  previewSessionArtifact(
    sessionId: string,
    artifactId: string,
    payload: { offset?: number; limit?: number } = {}
  ): Promise<ApiResponse<ArtifactPreviewResponse>> {
    return http.get<ArtifactPreviewResponse>(
      `/ai/chat/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}/preview`,
      payload
    )
  },
  /** 下载产物原文件字节（带鉴权），返回 Blob 由调用方触发浏览器保存。 */
  async downloadSessionArtifact(sessionId: string, artifactId: string): Promise<Blob> {
    return http.getBlob(
      `/ai/chat/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}/download`
    )
  }
}
